import { dependencies } from '@pn/core/dependencies';
import type { DataItemId } from '@pn/core/domain/data';
import type { Details, Document } from '@pn/core/domain/data-info';
import { createDataDetailsSlice } from '@pn/core/storage/data-info/dataDetailsSlice';
import { createDataDocumentsSlice } from '@pn/core/storage/data-info/dataDocumentsSlice';
import { useInjectableReducer } from '@pn/core/storage/injectableReducers';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { isNil } from 'lodash-es';

export const useDataDetailsStorage = (dataType: string) => {
  const { name, reducer, getInitialState } = createDataDetailsSlice(dataType);

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  useInjectableReducer(name, reducer);

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isError: useSliceSelector((state) => state.isError),
    idRequested: useSliceSelector((state) => state.idRequested),
    details: useSliceSelector((state) => state.details),
  };
};

export const dataDetailsActions = (dataType: string) => {
  const { dispatch, getState } = dependencies.store;

  const { name, actions } = createDataDetailsSlice(dataType);

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: (id: DataItemId) => dispatch(actions.request(id)),
    error: () => dispatch(actions.error()),
    receive: (details: Details) => dispatch(actions.receive(details)),
  };
};

export const useDataDocumentsStorage = (dataType: string) => {
  const { name, reducer, getInitialState } = createDataDocumentsSlice(dataType);

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  useInjectableReducer(name, reducer);

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isError: useSliceSelector((state) => state.isError),
    documents: useSliceSelector((state) => state.documents),
  };
};

export const dataDocumentsActions = (dataType: string) => {
  const { dispatch, getState } = dependencies.store;

  const { name, actions } = createDataDocumentsSlice(dataType);

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: () => dispatch(actions.request()),
    error: () => dispatch(actions.error()),
    receive: (documents: Document[]) => dispatch(actions.receive(documents)),
  };
};
